import { v4 as uuid } from 'uuid';
import {
  Box, Grid,
} from '@mui/material';
import { useCallback, useContext, useMemo } from 'react';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';
import Utils from '../../../utils/Utils';
import BlockProps from '../../../models/BlockProps';
import ImageComponent from '../../ImageComponent';
import { ImagesContext } from '../../../contexts/ImagesContext';
import ImageModal from '../../Modals/ImageModal/ImageModal';
import BlockData from '../../../models/BlockData';
import LinkButton from '../../Buttons/LinkButton';
import styles from './BlockCardCentered.module.css';
import BlockCardCenteredData, { BlockCardCenteredVariant } from '../../../models/Block/BlockCardCenteredData';
import useResponsive from '../../../hooks/UseResponsive';

export interface BlockCardCenteredProps extends BlockProps {
  blockData: BlockData[],
  isFullBleed?: boolean,
}

function BlockCardCentered({
  data, isEditing, isTemplate, blockData, isFullBleed = false, editHandler,
}: BlockCardCenteredProps) {
  const props = (data.data as BlockCardCenteredData);
  const { images } = useContext(ImagesContext);
  const cardItemId = uuid();

  const deleteImage = useCallback(() => {
    props.mainImage = undefined;
    editHandler(data.id, props);
  }, [data.id, editHandler, props]);

  useResponsive({
    items: [
      props.variant === BlockCardCenteredVariant.Left
        ? {
          wrapperId: `card-centered-wrapper-${cardItemId}`,
          targetId: `card-centered-text-${cardItemId}`,
          queries: [
            { size: 0, className: styles.textSideLeftSmartphonePreview },
            { size: 600, className: isFullBleed ? styles.textSideLeftBleed : styles.textSideLeft },
          ],
        } : {
          wrapperId: `card-centered-wrapper-${cardItemId}`,
          targetId: `card-centered-text-${cardItemId}`,
          queries: [
            { size: 0, className: styles.textSideRightSmartphonePreview },
            { size: 600, className: isFullBleed ? styles.textSideRightBleed : styles.textSideRight },
          ],
        },

      props.variant === BlockCardCenteredVariant.Right && {
        wrapperId: `card-centered-wrapper-${cardItemId}`,
        targetId: `card-centered-wrapper-${cardItemId}`,
        queries: [
          { size: 0, className: styles.containerSmartphonePreview },
          { size: 600, className: styles.container },
        ],
      },
    ],
  });

  const imageSide = useMemo(() => (
    <Grid
      item
      xs={6}
      minWidth={300}
    >

      {props.mainImage && (
        <Box sx={Utils.hoverBorder(isEditing)}>

          {isEditing && <CloseButton updateHandler={() => { deleteImage(); }} />}
          <ImageModal
            disabled={isTemplate || !isEditing}
            images={images}
            handleOnConfirm={(url: string) => {
              props.mainImage.value = url;
              editHandler(data.id, props);
            }}
          >
            <ImageComponent src={props.mainImage.value} />
          </ImageModal>
        </Box>
      )}

    </Grid>
  ), [props, isEditing, data.id, isTemplate, images, deleteImage, editHandler]);

  const textSide = useMemo(() => (
    <Grid
      item
      id={`card-centered-text-${cardItemId}`}
      xs={6}
      minWidth={300}
      alignItems="start"
      justifyContent="start"
    >

      {props.title && (
        <Grid
          item
          xs="auto"
          alignContent="start"
          justifyContent="start"
          sx={{
            paddingTop: 2,
            marginBottom: 2,
          }}
        >
          <Box sx={Utils.hoverBorder(isEditing)}>

            {isEditing && (
              <CloseButton updateHandler={() => {
                props.title = undefined;
                editHandler(data.id, props);
              }}
              />
            )}

            <EditableText
              text={props.title.value}
              isEditable={isEditing && !isTemplate}
              updateHandler={(text) => {
                props.title.value = text;
                editHandler(data.id, props);
              }}
            />
          </Box>
        </Grid>
      )}

      {props.description && (
        <Grid
          item
          xs="auto"
          sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}
        >

          {isEditing && (
            <CloseButton updateHandler={() => {
              props.description = undefined;
              editHandler(data.id, props);
            }}
            />
          )}

          <EditableText
            text={props.description.value}
            isEditable={isEditing && !isTemplate}
            updateHandler={(text) => {
              props.description.value = text;
              editHandler(data.id, props);
            }}
          />

        </Grid>
      )}

      {props.button && (
        <Box sx={Utils.hoverBorder(isEditing)}>
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.button = undefined;
              editHandler(data.id, props);
            }}
          />
          <LinkButton
            button={props.button}
            blockData={blockData}
            isEditable={isEditing && !isTemplate}
            isTemplate={isTemplate}
            updateHandler={(item) => {
              props.button = item;
              editHandler(data.id, props);
            }}
          />
        </Box>
      )}
    </Grid>
  ), [cardItemId, props, isEditing, isTemplate, blockData, editHandler, data.id]);

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: '250px',
        overflow: 'hidden',
        backgroundImage: props.backgroundImage ? `url(${props.backgroundImage.value})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: !isFullBleed || isEditing || isTemplate ? 2 : 0,
      }}
    >
      <Grid
        container
        spacing={0}
        direction="column"
        justifyContent="end"
      >

        {(isEditing && !isTemplate) && (
          <ImageModal
            title="背景設定"
            images={images}
            handleOnConfirm={(url: string) => {
              props.backgroundImage.value = url;
              editHandler(data.id, props);
            }}
          />
        )}

      </Grid>

      <Grid
        id={`card-centered-wrapper-${cardItemId}`}
        container
        spacing={0}
        direction="row"
        alignItems="center"
        justifyContent="center"
      >
        {props.variant === BlockCardCenteredVariant.Left ? (
          <>
            {imageSide}
            {textSide}
          </>
        ) : (
          <>
            {textSide}
            {imageSide}
          </>
        )}
      </Grid>

    </Grid>
  );
}

export default BlockCardCentered;
